import { useMemo, useState } from 'react';
import { Loader2, X, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadClassworkResultsExport,
  type ClassworkAssignment,
  type ClassworkResultsDocType
} from '../../lib/classworkApi';

type DocTypeOption = {
  id: ClassworkResultsDocType;
  title: string;
  description: string;
};

const DOC_TYPES: DocTypeOption[] = [
  {
    id: 'overview_rough',
    title: 'Kurs-/Klassenübersicht (grob)',
    description: 'Tabelle mit Name, Vorname, Klasse, LRS, Punkten, Prozent und Note je Schüler:in.'
  },
  {
    id: 'overview_matrix',
    title: 'Kurs-/Klassenübersicht (fein / Matrix)',
    description: 'Punktematrix: erreichte Punkte pro Aufgabe aus dem Korrekturraster für alle Schüler:innen.'
  },
  {
    id: 'student_summary',
    title: 'Schüler-Kurzbrief',
    description: 'Eine Seite pro Schüler:in mit Note, Punkten und der pädagogisch vereinfachten Rückmeldung.'
  },
  {
    id: 'student_detail',
    title: 'Schüler-Detailbeurteilung',
    description: 'Detailbericht pro Schüler:in: Aufgabenliste, Stärken, Verbesserungen, Tipps, Lehrerkommentar.'
  }
];

const GRADED_STATES = new Set(['graded', 'published']);

type EligibleRow = {
  assignmentId: number;
  firstName: string;
  lastName: string;
  className: string;
  state: string;
  gradeValue: string | null;
  pointsAchieved: number | null;
  pointsMax: number | null;
};

interface Props {
  open: boolean;
  classworkId: number;
  classworkTitle?: string | null;
  assignments: Array<
    Pick<ClassworkAssignment, 'id' | 'student_id' | 'first_name' | 'last_name' | 'class_name' | 'state'>
  >;
  gradeRowByAssignmentId: Record<number, any>;
  onClose: () => void;
}

function formatPoints(value: number | null, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '–';
  const rounded = Math.round(value * Math.pow(10, digits)) / Math.pow(10, digits);
  return rounded.toFixed(digits).replace('.', ',');
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ClassworkResultsExportModal({
  open,
  classworkId,
  classworkTitle,
  assignments,
  gradeRowByAssignmentId,
  onClose
}: Props): JSX.Element | null {
  const [selectedDocTypes, setSelectedDocTypes] = useState<Set<ClassworkResultsDocType>>(
    () => new Set<ClassworkResultsDocType>(['overview_rough'])
  );
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const eligibleRows: EligibleRow[] = useMemo(() => {
    const rows: EligibleRow[] = [];
    for (const a of assignments) {
      if (!GRADED_STATES.has(String(a.state || '').toLowerCase())) continue;
      const grade = gradeRowByAssignmentId[Number(a.id)] || null;
      rows.push({
        assignmentId: Number(a.id),
        firstName: String(a.first_name || '').trim(),
        lastName: String(a.last_name || '').trim(),
        className: String(a.class_name || '').trim(),
        state: String(a.state || ''),
        gradeValue: grade?.grade_value ? String(grade.grade_value) : null,
        pointsAchieved:
          grade?.points_achieved != null && Number.isFinite(Number(grade.points_achieved))
            ? Number(grade.points_achieved)
            : null,
        pointsMax:
          grade?.points_max != null && Number.isFinite(Number(grade.points_max))
            ? Number(grade.points_max)
            : null
      });
    }
    rows.sort((a, b) => {
      const c = (a.className || '').localeCompare(b.className || '', 'de');
      if (c !== 0) return c;
      const n = (a.lastName || '').localeCompare(b.lastName || '', 'de');
      if (n !== 0) return n;
      return (a.firstName || '').localeCompare(b.firstName || '', 'de');
    });
    return rows;
  }, [assignments, gradeRowByAssignmentId]);

  // Initial selection: all eligible rows selected on first render while modal opens.
  const eligibleKey = useMemo(() => eligibleRows.map((r) => r.assignmentId).join(','), [eligibleRows]);
  const [lastInitKey, setLastInitKey] = useState<string | null>(null);
  if (open && lastInitKey !== eligibleKey) {
    setLastInitKey(eligibleKey);
    setSelectedAssignmentIds(new Set(eligibleRows.map((r) => r.assignmentId)));
  }
  if (!open && lastInitKey != null) {
    setLastInitKey(null);
  }

  if (!open) return null;

  const toggleDocType = (id: ClassworkResultsDocType) => {
    setSelectedDocTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAssignment = (id: number) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedAssignmentIds(new Set(eligibleRows.map((r) => r.assignmentId)));
  const selectNone = () => setSelectedAssignmentIds(new Set());

  const handleSubmit = async () => {
    if (selectedDocTypes.size === 0) {
      toast.error('Bitte mindestens einen Dokumenttyp wählen.');
      return;
    }
    if (selectedAssignmentIds.size === 0) {
      toast.error('Bitte mindestens einen Schüler bzw. eine Schülerin wählen.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await downloadClassworkResultsExport(classworkId, {
        documents: Array.from(selectedDocTypes),
        assignment_ids: Array.from(selectedAssignmentIds)
      });
      triggerBrowserDownload(result.blob, result.filename);
      toast.success(
        selectedDocTypes.size === 1 ? 'PDF wurde heruntergeladen.' : 'ZIP mit PDFs wurde heruntergeladen.'
      );
      onClose();
    } catch (error: any) {
      let message = 'PDF-Export fehlgeschlagen';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) message = String(parsed.error);
        } catch {
          /* ignore */
        }
      } else if (typeof data?.error === 'string') {
        message = data.error;
      } else if (typeof error?.message === 'string') {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && selectedDocTypes.size > 0 && selectedAssignmentIds.size > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
      onClick={() => (submitting ? null : onClose())}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl bg-white shadow-2xl border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Ergebnisse als PDF exportieren</h3>
            {classworkTitle ? (
              <p className="text-xs text-gray-600 mt-0.5 truncate">{classworkTitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40"
            onClick={onClose}
            disabled={submitting}
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          {/* Step 1: document types */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">1. Dokumenttypen</h4>
            <p className="text-xs text-gray-600 mb-3">
              Bei Mehrfachauswahl wird ein ZIP-Archiv mit je einer PDF pro Typ erzeugt.
            </p>
            <ul className="space-y-2">
              {DOC_TYPES.map((opt) => {
                const checked = selectedDocTypes.has(opt.id);
                return (
                  <li key={opt.id}>
                    <label
                      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        checked ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={checked}
                        onChange={() => toggleDocType(opt.id)}
                        disabled={submitting}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900">{opt.title}</span>
                        <span className="block text-xs text-gray-600 mt-0.5">{opt.description}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Step 2: student selection */}
          <section>
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                2. Schüler:innen ({selectedAssignmentIds.size} / {eligibleRows.length})
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-xs btn-secondary"
                  onClick={selectAll}
                  disabled={submitting || eligibleRows.length === 0}
                >
                  Alle
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-secondary"
                  onClick={selectNone}
                  disabled={submitting || selectedAssignmentIds.size === 0}
                >
                  Keine
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Es werden nur Abgaben mit finalisierter oder veröffentlichter Bewertung angezeigt.
            </p>
            {eligibleRows.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm p-3">
                Es liegen noch keine bewerteten Abgaben für diese Klassenarbeit vor.
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 max-h-72 overflow-y-auto">
                {eligibleRows.map((row) => {
                  const checked = selectedAssignmentIds.has(row.assignmentId);
                  const pointsLabel =
                    row.pointsAchieved != null && row.pointsMax != null
                      ? `${formatPoints(row.pointsAchieved)} / ${formatPoints(row.pointsMax)} Pkt.`
                      : null;
                  return (
                    <label
                      key={row.assignmentId}
                      className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${
                        checked ? 'bg-primary-50/40' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={checked}
                        onChange={() => toggleAssignment(row.assignmentId)}
                        disabled={submitting}
                      />
                      <span className="flex-1 min-w-0 truncate">
                        <span className="font-medium text-gray-900">
                          {row.lastName || '—'}, {row.firstName || '—'}
                        </span>
                        {row.className ? (
                          <span className="ml-2 text-xs text-gray-500">{row.className}</span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-3 shrink-0 text-xs text-gray-600 tabular-nums">
                        {pointsLabel ? <span>{pointsLabel}</span> : null}
                        <span className="inline-flex items-center justify-center min-w-[28px] rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-gray-800 font-semibold">
                          {row.gradeValue || '–'}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4 sm:p-5">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary inline-flex items-center gap-2"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            {submitting ? 'Erzeuge …' : 'PDF erzeugen'}
          </button>
        </div>
      </div>
    </div>
  );
}
